import _ from 'lodash';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import * as yup from 'yup';
import { Loader2, CheckCircle2, ArrowDown, Download } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Skeleton } from '../ui/skeleton';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import ValidationMessage from '../utils/ValidationMessage';
import { cn } from 'src/lib/utils';

import {
  getCatalogueItem,
  getMarkdown,
  isAnyLoading,
  isDocLoading,
  formatDocumentKind,
  formatLegalDocumentKind,
  buildAcceptPayload,
  estimateReadMinutes,
  joinTitles,
} from './agreementsUtils';

/**
 * Shared agreements modal used by the Org and User scoped wrappers, as well as
 * the footer single-document viewer. Progressive-disclosure accordion; only
 * enables the Confirm button after every outstanding agreement has been opened
 * and the consent checkbox is ticked.
 *
 * Props:
 * - scope: 'org' | 'user'
 * - outstandingDocs, catalogueItems, markdownCache, loading, updatedDocInfo, wlSettings, branding
 * - dismissable, onDismiss
 * - fetchLegalMarkdown(kind, versionLabel)
 * - acceptLegalDocuments(documents)
 * - onInviteSignatory(docKinds)  // scope='org' only
 * - Footer single-doc viewer props (optional):
 *     isFooterAgreementOpen, legalAgreement ('tncs' | 'privacy'), onCloseFooterAgreement
 */
const TABLE_MD_COMPONENTS = {
  table: ({ node, ...props }) => (
    <div className='overflow-x-auto -mx-1 my-4'>
      <table className='w-full border-collapse text-sm' {...props} />
    </div>
  ),
};

// Explicit spacing utilities for the rendered markdown body. The project does
// not ship `@tailwindcss/typography`, so `prose` alone leaves paragraphs/lists
// with no vertical rhythm. These arbitrary-child selectors provide that.
const markdownProseClasses = cn(
  'userAgreement-text text-sm leading-relaxed',
  '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
  '[&_p]:my-3 [&_p]:leading-relaxed',
  '[&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-5 [&_h1]:mb-2',
  '[&_h2]:text-[0.95rem] [&_h2]:font-semibold [&_h2]:mt-5 [&_h2]:mb-2',
  '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1.5',
  '[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:mb-1',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-3',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-3',
  '[&_li]:my-1.5 [&_li]:leading-relaxed',
  '[&_strong]:font-semibold',
  '[&_em]:italic',
  '[&_a]:text-primary [&_a]:underline hover:[&_a]:no-underline',
  '[&_code]:bg-muted [&_code]:text-foreground [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[0.9em]',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:my-3',
  '[&_hr]:my-5 [&_hr]:border-border',
  '[&_th]:border [&_th]:border-border [&_th]:px-2.5 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold [&_th]:bg-muted/40',
  '[&_td]:border [&_td]:border-border [&_td]:px-2.5 [&_td]:py-1.5 [&_td]:align-top',
);

// Standalone stylesheet for the printed document. Mirrors the modal's
// on-screen typography so the saved PDF looks like what the user reviewed.
const DOWNLOAD_CSS = `
  @page { margin: 18mm; }
  body { margin: 0; padding: 0; color: #1a1a1a; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size: 12pt; line-height: 1.6; }
  .doc-header { border-bottom: 1px solid #e5e5e5; padding-bottom: 0.75rem; margin-bottom: 1.5rem; }
  .doc-title { font-size: 1.5rem; font-weight: 600; margin: 0; }
  .doc-version { font-size: 0.85rem; font-weight: 400; color: #6b7280; margin-left: 0.5rem; }
  h1 { font-size: 1.1rem; font-weight: 600; margin: 1.5rem 0 0.5rem; }
  h2 { font-size: 1.02rem; font-weight: 600; margin: 1.5rem 0 0.5rem; }
  h3 { font-size: 0.95rem; font-weight: 600; margin: 1.1rem 0 0.4rem; }
  h4 { font-size: 0.92rem; font-weight: 600; margin: 0.9rem 0 0.3rem; }
  p { margin: 0.75rem 0; }
  ul { list-style: disc; padding-left: 1.4rem; margin: 0.75rem 0; }
  ol { list-style: decimal; padding-left: 1.4rem; margin: 0.75rem 0; }
  li { margin: 0.4rem 0; }
  strong { font-weight: 600; }
  em { font-style: italic; }
  a { color: #1d4ed8; text-decoration: underline; }
  code { background: #f3f4f6; padding: 0.1rem 0.25rem; border-radius: 0.25rem; font-size: 0.9em; }
  blockquote { border-left: 2px solid #e5e5e5; padding-left: 0.75rem; font-style: italic; margin: 0.75rem 0; }
  hr { border: none; border-top: 1px solid #e5e5e5; margin: 1.5rem 0; }
  table { width: 100%; border-collapse: collapse; font-size: 0.9rem; margin: 1rem 0; }
  th, td { border: 1px solid #e5e5e5; padding: 0.4rem 0.6rem; text-align: left; vertical-align: top; }
  th { background: #f9fafb; font-weight: 600; }
`;

const escapeHtml = (value) =>
  String(value || '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
  );

/**
 * Render the document markdown into a hidden iframe styled to match the modal,
 * then invoke the browser print dialog so the user can save it as a PDF. The
 * document `title` becomes the default PDF filename.
 */
const downloadDocumentPdf = ({ title, version, markdown }) => {
  const body = renderToStaticMarkup(
    <ReactMarkdown remarkPlugins={[gfm]} components={TABLE_MD_COMPONENTS}>
      {markdown}
    </ReactMarkdown>,
  );
  const versionMarkup = version ? `<span class="doc-version">v${escapeHtml(version)}</span>` : '';
  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}${version ? ` v${escapeHtml(version)}` : ''}</title>
<style>${DOWNLOAD_CSS}</style>
</head>
<body>
<div class="doc-header"><span class="doc-title">${escapeHtml(title)}</span>${versionMarkup}</div>
${body}
</body>
</html>`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.srcdoc = html;

  iframe.onload = () => {
    const frameWindow = iframe.contentWindow;
    if (!frameWindow) {
      iframe.remove();
      return;
    }
    const cleanup = () => setTimeout(() => iframe.remove(), 1000);
    frameWindow.onafterprint = cleanup;
    frameWindow.focus();
    frameWindow.print();
    // Fallback cleanup in case onafterprint never fires (e.g. dialog cancelled).
    setTimeout(cleanup, 60000);
  };

  document.body.appendChild(iframe);
};

const AgreementsModal = ({
  scope,
  outstandingDocs,
  catalogueItems,
  markdownCache,
  loading,
  updatedDocInfo,
  wlSettings,
  dismissable,
  onDismiss,
  fetchLegalMarkdown,
  acceptLegalDocuments,
  onInviteSignatory,
  isFooterAgreementOpen,
  legalAgreement,
  onCloseFooterAgreement,
}) => {
  const { name, whiteLabelId } = wlSettings || {};
  const isWhiteLabelApp = whiteLabelId ? whiteLabelId !== '0' : undefined;

  const [consentChecked, setConsentChecked] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  const [openedDocKinds, setOpenedDocKinds] = useState(() => new Set());
  const [openValue, setOpenValue] = useState(null);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        consentChecked: yup
          .boolean()
          .oneOf([true], 'Please tick the box to confirm you have read and agree.')
          .required(),
      }),
    [],
  );

  // ----- Fetch markdown (batch via refs to avoid refetching) -----
  const fetchedRef = useRef(new Set());

  const outstandingKey = (outstandingDocs || []).map((d) => d.documentKind).sort().join(',');
  const catalogueKey = (catalogueItems || []).map((i) => `${i.documentKind}:${i.versionLabel}`).sort().join(',');

  useEffect(() => {
    if (isFooterAgreementOpen) {
      const docKind = legalAgreement === 'tncs' ? 'terms_of_service' : 'privacy_policy';
      const item = getCatalogueItem(catalogueItems, docKind);
      const key = `${docKind}@${item?.versionLabel || 'latest'}`;
      if (!fetchedRef.current.has(key)) {
        fetchedRef.current.add(key);
        fetchLegalMarkdown(docKind, item?.versionLabel || null);
      }
      return;
    }
    (outstandingDocs || []).forEach((doc) => {
      const item = getCatalogueItem(catalogueItems, doc.documentKind);
      const key = `${doc.documentKind}@${item?.versionLabel || 'latest'}`;
      if (!fetchedRef.current.has(key)) {
        fetchedRef.current.add(key);
        fetchLegalMarkdown(doc.documentKind, item?.versionLabel || null);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outstandingKey, catalogueKey, isFooterAgreementOpen, legalAgreement]);

  // ----- Per-item refs for scroll-into-view -----
  const itemRefs = useRef({});
  const setItemRef = useCallback(
    (docKind) => (el) => {
      if (el) itemRefs.current[docKind] = el;
      else delete itemRefs.current[docKind];
    },
    [],
  );

  const scrollToItem = useCallback((docKind) => {
    const el = itemRefs.current[docKind];
    if (!el) return;
    // Wait one frame so the accordion has started expanding before we scroll.
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleOpenChange = useCallback(
    (nextValue) => {
      setOpenValue(nextValue);
      if (nextValue) {
        setOpenedDocKinds((prev) => {
          if (prev.has(nextValue)) return prev;
          const next = new Set(prev);
          next.add(nextValue);
          return next;
        });
        scrollToItem(nextValue);
      }
    },
    [scrollToItem],
  );

  const openAndScrollTo = useCallback(
    (docKind) => {
      if (!docKind) return;
      handleOpenChange(docKind);
    },
    [handleOpenChange],
  );

  const handleDownload = useCallback(
    (documentKind) => {
      const item = getCatalogueItem(catalogueItems, documentKind);
      const markdown = getMarkdown(markdownCache, catalogueItems, documentKind);
      if (!markdown) return;
      downloadDocumentPdf({
        title: item?.title || formatLegalDocumentKind(documentKind),
        version: item?.versionLabel,
        markdown,
      });
    },
    [catalogueItems, markdownCache],
  );

  // ----- Accept flow -----
  const confirm = async () => {
    try {
      await validationSchema.validate({ consentChecked }, { abortEarly: false });
    } catch (error) {
      setValidationError(error.inner);
      return;
    }

    setAccepting(true);
    setAcceptError(null);
    const documents = buildAcceptPayload(outstandingDocs, catalogueItems);
    const result = await acceptLegalDocuments(documents);
    setAccepting(false);

    if (!result) return;

    const results = result?.results || [];
    const hashMismatched = results.filter((r) => r.error === 'hash_mismatch');
    const notAuthorised = results.filter((r) => r.error === 'not_authorised_to_sign');
    const notFound = results.filter((r) => r.error === 'document_not_found');

    if (hashMismatched.length > 0) {
      setAcceptError(
        'The document has been updated since you opened it. We are refreshing the content — please review and try again.',
      );
      hashMismatched.forEach((r) => {
        const item = getCatalogueItem(catalogueItems, r.documentKind);
        if (item?.versionLabel) fetchLegalMarkdown(r.documentKind, item.versionLabel);
      });
      return;
    }

    if (notAuthorised.length > 0) {
      if (scope === 'org' && onInviteSignatory) {
        onInviteSignatory(notAuthorised.map((r) => r.documentKind));
      }
      if (results.some((r) => r.ok)) {
        setAcceptError(
          'Some agreements were accepted, but you do not have permission to sign all of them. Please invite a legal signatory.',
        );
      } else {
        setAcceptError(
          'You do not have permission to sign these agreements. Please invite a legal signatory.',
        );
      }
      return;
    }

    if (notFound.length > 0) {
      setAcceptError(
        'Some documents could not be found. This may be a configuration issue — please try again later.',
      );
    }
  };

  // =====================================================================
  // Footer single-doc viewer (terms_of_service / privacy_policy preview)
  // =====================================================================
  if (isFooterAgreementOpen) {
    const docKind = legalAgreement === 'tncs' ? 'terms_of_service' : 'privacy_policy';
    const title = legalAgreement === 'tncs' ? 'Software License Agreement' : 'Privacy Policy';
    const markdown = getMarkdown(markdownCache, catalogueItems, docKind);
    const docLoading = isDocLoading(loading, catalogueItems, docKind);

    return (
      <Dialog open onOpenChange={() => onCloseFooterAgreement && onCloseFooterAgreement()}>
        <DialogContent
          className='max-w-3xl max-h-[90vh] flex flex-col'
          onClose={() => onCloseFooterAgreement && onCloseFooterAgreement()}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {isWhiteLabelApp && (
              <p className='text-sm text-muted-foreground pt-1'>Powered by CYF Impact Ltd for {name}.</p>
            )}
          </DialogHeader>
          <DialogBody className='overflow-y-auto flex-1'>
            <div className='text-sm text-foreground'>
              {docLoading ? (
                <div className='space-y-2'>
                  <Skeleton className='h-4 w-2/3' />
                  <Skeleton className='h-4 w-5/6' />
                  <Skeleton className='h-4 w-3/4' />
                  <Skeleton className='h-4 w-full' />
                </div>
              ) : markdown ? (
                <ReactMarkdown
                  remarkPlugins={[gfm]}
                  components={TABLE_MD_COMPONENTS}
                  className={markdownProseClasses}>
                  {markdown}
                </ReactMarkdown>
              ) : (
                <p className='text-muted-foreground italic py-4'>Document content is not yet available.</p>
              )}
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    );
  }

  // =====================================================================
  // Full acceptance flow (org / user)
  // =====================================================================
  const outstandingKinds = (outstandingDocs || []).map((d) => d.documentKind);
  const allOpened = outstandingKinds.length > 0 && outstandingKinds.every((k) => openedDocKinds.has(k));
  const loadingAny = isAnyLoading(loading, catalogueItems, outstandingKinds);

  const outstandingTitles = (outstandingDocs || []).map((doc) => {
    const item = getCatalogueItem(catalogueItems, doc.documentKind);
    return item?.title || formatLegalDocumentKind(doc.documentKind);
  });

  const consentLabel =
    scope === 'user'
      ? 'I have read and agree to the User License Agreement and Privacy Policy.'
      : outstandingTitles.length > 0
        ? `I have read and agree to the ${joinTitles(outstandingTitles)}.`
        : 'I have read and agree to the agreements above.';

  const hasUpdatedDocs = (updatedDocInfo || []).length > 0;
  const reasonLine = hasUpdatedDocs
    ? 'We have updated these agreements. Please review and accept to continue.'
    : scope === 'user'
      ? 'Please review and accept these agreements to continue using your account.'
      : 'Please review and accept these agreements to continue using your organisation.';

  const confirmDisabled = !allOpened || !consentChecked || accepting;
  const readCount = outstandingKinds.filter((k) => openedDocKinds.has(k)).length;
  const totalCount = outstandingKinds.length;
  const nextUnreadKind = outstandingKinds.find((k) => !openedDocKinds.has(k)) || null;
  const progressLine =
    totalCount > 1 ? `${readCount} of ${totalCount} agreements reviewed` : null;
  const helperText = !allOpened
    ? progressLine || 'Open the agreement to continue.'
    : !consentChecked
      ? 'Tick the box above to continue.'
      : null;

  return (
    <Dialog open onOpenChange={dismissable && onDismiss ? onDismiss : () => {}}>
      <DialogContent
        className='max-w-3xl max-h-[90vh] flex flex-col'
        onInteractOutside={(e) => {
          if (!dismissable) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (!dismissable) e.preventDefault();
        }}
        onClose={dismissable && onDismiss ? onDismiss : undefined}>
        <DialogHeader>
          <DialogTitle>
            {scope === 'user' ? 'User License Agreement and Privacy Policy' : 'Organisation Agreements'}
          </DialogTitle>
          {isWhiteLabelApp === false ? (
            <div className='flex justify-center pt-3'>
              <img
                src='https://s3.eu-west-2.amazonaws.com/downloads.compareyourfootprint.com/branding/compareyourfootprint/Picture+1.jpg'
                alt='CYF'
                className='max-h-12'
              />
            </div>
          ) : isWhiteLabelApp ? (
            <p className='text-sm text-muted-foreground pt-1'>Powered by CYF Impact Ltd for {name}.</p>
          ) : null}
          <p className='text-sm text-muted-foreground pt-2'>{reasonLine}</p>
        </DialogHeader>

        <DialogBody className='overflow-y-auto flex-1'>
          {hasUpdatedDocs && (
            <div className='bg-info-bg text-info border border-info/20 rounded-lg p-3 mb-4 text-sm'>
              {updatedDocInfo.map(({ documentKind, title, graceDays }) => (
                <p key={documentKind} className='mb-1 last:mb-0'>
                  Our <strong>{title}</strong> has been updated. Please review and accept the new version.
                  {graceDays > 0 && ` You have ${graceDays} day${graceDays !== 1 ? 's' : ''} to review and accept.`}
                </p>
              ))}
            </div>
          )}

          {loadingAny && openedDocKinds.size === 0 && (
            <div className='flex items-center gap-2 text-xs text-muted-foreground mb-3'>
              <Loader2 size={14} strokeWidth={1.5} className='animate-spin' />
              Loading agreements…
            </div>
          )}

          <Accordion type='single' collapsible value={openValue} onValueChange={handleOpenChange}>
            {(outstandingDocs || []).map((doc) => {
              const item = getCatalogueItem(catalogueItems, doc.documentKind);
              const markdown = getMarkdown(markdownCache, catalogueItems, doc.documentKind);
              const docLoading = isDocLoading(loading, catalogueItems, doc.documentKind);
              const opened = openedDocKinds.has(doc.documentKind);
              const title = item?.title || formatLegalDocumentKind(doc.documentKind);
              const version = item?.versionLabel;
              const readMins = estimateReadMinutes(markdown);

              return (
                <AccordionItem
                  key={doc.documentKind}
                  value={doc.documentKind}
                  ref={setItemRef(doc.documentKind)}>
                <AccordionTrigger
                  actions={
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-8 w-8 text-muted-foreground hover:text-foreground'
                      disabled={docLoading || !markdown}
                      aria-label={`Download ${title} as PDF`}
                      title={`Download ${title} as PDF`}
                      onClick={() => handleDownload(doc.documentKind)}>
                      <Download size={16} strokeWidth={1.5} />
                    </Button>
                  }>
                  <span className='flex flex-wrap items-center gap-x-3 gap-y-1'>
                    <span className='font-semibold text-foreground'>{title}</span>
                    {version && (
                      <span className='text-xs text-muted-foreground font-normal'>v{version}</span>
                    )}
                    {readMins != null && (
                      <span className='text-xs text-muted-foreground font-normal'>~{readMins} min read</span>
                    )}
                    {opened && (
                      <span className='inline-flex items-center gap-1 text-xs text-success font-normal'>
                        <CheckCircle2 size={12} strokeWidth={2} />
                        Read
                      </span>
                    )}
                  </span>
                </AccordionTrigger>
                  <AccordionContent>
                    {docLoading ? (
                      <div className='space-y-2 py-1'>
                        <Skeleton className='h-4 w-2/3' />
                        <Skeleton className='h-4 w-5/6' />
                        <Skeleton className='h-4 w-3/4' />
                        <Skeleton className='h-4 w-full' />
                        <Skeleton className='h-4 w-4/6' />
                      </div>
                    ) : markdown ? (
                      <div className='text-sm text-foreground'>
                        <ReactMarkdown
                          remarkPlugins={[gfm]}
                          components={TABLE_MD_COMPONENTS}
                          className={markdownProseClasses}>
                          {markdown}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className='text-muted-foreground italic text-sm py-2'>
                        Document content is not yet published.
                      </p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </DialogBody>

        <div className='px-7 py-4 border-t border-border flex-none'>
          {acceptError && (
            <div className='bg-danger-bg text-danger border border-danger/20 rounded-lg p-3 mb-3 text-sm'>
              {acceptError}
            </div>
          )}

          <div className='flex items-start gap-2 mb-2'>
            <Checkbox
              id='accept-agreements'
              checked={consentChecked}
              onCheckedChange={(v) => {
                setConsentChecked(Boolean(v));
                if (v) setValidationError(null);
              }}
              className='mt-0.5'
            />
            <label htmlFor='accept-agreements' className='text-sm text-foreground leading-snug cursor-pointer'>
              {consentLabel}
            </label>
          </div>

          <ValidationMessage message={_.find(validationError, { path: 'consentChecked' })?.message} />

          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-2'>
            <p className='text-xs text-muted-foreground min-h-[1rem]' aria-live='polite'>
              {helperText}
            </p>
            <div className='flex flex-wrap items-center justify-end gap-2'>
              {scope === 'org' && onInviteSignatory && acceptError && (
                <Button size='lg' variant='outline' onClick={() => onInviteSignatory(outstandingKinds)}>
                  Invite Legal Signatory
                </Button>
              )}
              {!allOpened && nextUnreadKind && (
                <Button
                  size='lg'
                  variant='outline'
                  onClick={() => openAndScrollTo(nextUnreadKind)}>
                  <ArrowDown size={16} strokeWidth={1.75} />
                  {readCount === 0 ? 'Review first agreement' : 'Review next agreement'}
                </Button>
              )}
              <Button
                size='lg'
                loading={accepting}
                disabled={confirmDisabled}
                aria-disabled={confirmDisabled}
                onClick={confirm}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgreementsModal;
